import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatARS } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { QtyStepper } from "./QtyStepper";
import { OptionRow } from "./OptionRow";
import { toast } from "sonner";

export const ProductBuilder = ({ product, category, siblings, settings, onClose }) => {
  const { add } = useCart();
  const [mode, setMode] = useState("entera");
  const [halfId, setHalfId] = useState("");
  const [dough, setDough] = useState(0);
  const [extras, setExtras] = useState([]);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setMode("entera"); setHalfId(""); setDough(0); setExtras([]); setQty(1);
  }, [product]);

  if (!product || !category) return null;
  const halves = !!category.half_and_half;
  const doughs = category.has_doughs ? settings.doughs || [] : [];
  const extraOpts = category.extras || [];
  const half = siblings.find((p) => p.id === halfId);
  const isHalf = halves && mode === "mitad";
  const base = isHalf && half ? Math.max(product.price, half.price) : product.price;
  const unit = base + (doughs[dough]?.price || 0) + extras.reduce((s, i) => s + (extraOpts[i]?.price || 0), 0);
  const others = siblings.filter((p) => p.id !== product.id && p.available);

  const toggleExtra = (i) => setExtras((e) => (e.includes(i) ? e.filter((x) => x !== i) : [...e, i]));

  const confirm = () => {
    if (isHalf && !half) return toast.error("Elegí el segundo gusto");
    const name = isHalf ? `Mitad ${product.name} / Mitad ${half.name}` : product.name;
    const details = [];
    if (doughs[dough]) details.push(doughs[dough].name);
    [...extras].sort().forEach((i) => details.push(`+ ${extraOpts[i].name}`));
    add({ key: `${name}|${details.join("|")}`, name, details, unit_price: unit, qty, image: product.image });
    toast.success(`${product.name} agregado al pedido`);
    onClose();
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1C1515] border-[#2E2323] text-amber-50 p-0 max-w-lg max-h-[92vh] overflow-y-auto" data-testid="pizza-builder">
        {product.image && <img src={product.image} alt={product.name} className="w-full h-40 object-cover" />}
        <div className="px-5 pb-5 space-y-5">
          <DialogHeader className="text-left">
            <DialogTitle className="text-2xl font-extrabold" data-testid="builder-title">{product.name}</DialogTitle>
            <DialogDescription className="text-[#A89F91]">{product.description || "Personalizá tu pedido"}</DialogDescription>
          </DialogHeader>

          {halves && (
            <Section title="Tamaño / Gustos">
              <div className="grid grid-cols-2 gap-2">
                {[["entera", "Entera"], ["mitad", "Mitad y Mitad"]].map(([v, l]) => (
                  <button key={v} onClick={() => setMode(v)} data-testid={`pizza-mode-${v}`}
                    className={`py-3 rounded-xl text-sm font-semibold border transition-colors ${mode === v ? "bg-[#DC2626] border-[#DC2626] text-white" : "border-[#2E2323] bg-[#120E0E] text-[#A89F91]"}`}>
                    {l}
                  </button>
                ))}
              </div>
              {isHalf && (
                <div className="mt-3 space-y-2" data-testid="pizza-half-options">
                  <p className="text-xs text-[#A89F91]">Mitad 1: <b className="text-amber-300">{product.name}</b> · Elegí la mitad 2 (se cobra el gusto de mayor precio):</p>
                  {others.map((p) => (
                    <OptionRow key={p.id} type="radio" checked={halfId === p.id} onClick={() => setHalfId(p.id)} label={p.name} price={formatARS(p.price)} testid={`pizza-half-${p.id}`} />
                  ))}
                </div>
              )}
            </Section>
          )}

          {doughs.length > 0 && (
            <Section title="Tipo de masa">
              {doughs.map((d, i) => (
                <OptionRow key={i} type="radio" checked={dough === i} onClick={() => setDough(i)} label={d.name} price={d.price ? `+ ${formatARS(d.price)}` : "Sin cargo"} testid={`pizza-dough-${i}`} />
              ))}
            </Section>
          )}

          {extraOpts.length > 0 && (
            <Section title="Agregados / Extras">
              {extraOpts.map((x, i) => (
                <OptionRow key={i} type="check" checked={extras.includes(i)} onClick={() => toggleExtra(i)} label={x.name} price={`+ ${formatARS(x.price)}`} testid={`pizza-extra-${i}`} />
              ))}
            </Section>
          )}

          <div className="sticky bottom-0 -mx-5 px-5 pt-3 pb-1 bg-[#1C1515] border-t border-[#2E2323] flex items-center gap-3">
            <QtyStepper value={qty} onChange={setQty} testid="pizza-qty" />
            <button onClick={confirm} data-testid="pizza-add-to-cart-btn"
              className="flex-1 py-3.5 rounded-full bg-[#DC2626] hover:bg-[#991B1B] text-white font-bold flex items-center justify-between px-5 transition-colors active:scale-[0.98]">
              <span>Agregar</span>
              <span className="font-price text-xl" data-testid="pizza-total-price">{formatARS(unit * qty)}</span>
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h4 className="text-xs uppercase tracking-[0.2em] text-amber-400 font-semibold mb-2">{title}</h4>
    <div className="space-y-2">{children}</div>
  </div>
);
